import { useState } from 'react';
import {
    BookOpenIcon,
    CameraIcon,
    Eraser as EraserIcon,
    RouterIcon,
    SirenIcon,
    TriangleAlertIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { useHubStatusHistory } from '@/services/hub.service';
import { HubMonitoring, HubSlot } from '@/services/field.service';
import { HubStatusHistoryModel } from '@/types/models';
import HubLogViewer from './HubLogViewer';
import HubSchedule from './HubSchedule';
import {
    HubIncidentsDialog,
    HubPicturesDialog,
    HubSettingsDialog,
} from './HubDialogs';
import HubStatusCleanup from './HubStatusCleanup';
import { WAKE_UP_GRACE_MS, roughDuration, useNow, utcStamp } from './fieldTime';
import { TH, TR, TD } from '../../components/BackofficePage';
import {
    DotRow,
    DotStrip,
    SAMPLE_INDENT_PX,
    ScrollSync,
    useWheelPan,
} from './FieldStrip';

/** The hub's single row of dots: did it report a status for each wake-up? */
const STATUS_ROW: DotRow<HubSlot>[] = [
    {
        label: 'status',
        dot: (slot) => ({ color: slot.status, title: statusTitle(slot) }),
    },
];

function statusTitle(slot: HubSlot): string {
    // Blank: either an unplanned column this hub had nothing in, or a slot from
    // before its first ever status, where there is nothing to report either way.
    if (slot.status === 'none') return '';
    // A hub woken by hand acquires nothing, so say which kind of wake-up this
    // was: it is the difference between a hub nobody asked for anything from
    // and a hub that turned up and did not deliver.
    const kind = slot.configMode ? 'config wake-up' : 'status';
    if (!slot.scheduled) {
        return slot.reportedAt
            ? `off-schedule ${kind} reported ${utcStamp(slot.reportedAt, true)}`
            : '';
    }
    return slot.reportedAt
        ? `scheduled ${utcStamp(slot.time)} · ${kind} reported ${utcStamp(slot.reportedAt, true)}`
        : `scheduled ${utcStamp(slot.time)} · no status reported`;
}

/**
 * A hub's identity and latest telemetry, with its logs and status history.
 * `sync` ties its dot strip to the samples below it, so the group scrolls as
 * one and every acquisition lines up across the linked cards.
 */
export default function HubCard({
    hub,
    sync,
}: {
    hub: HubMonitoring;
    sync: ScrollSync;
}) {
    const [showHistory, setShowHistory] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    const [showLogs, setShowLogs] = useState(false);
    const [showImages, setShowImages] = useState(false);
    const [showIncidents, setShowIncidents] = useState(false);
    const [showCleanup, setShowCleanup] = useState(false);
    const cardRef = useWheelPan<HTMLDivElement>(sync);
    // Checked against a clock that keeps running: the wake-up goes past while
    // the page sits open, and the card has to notice on its own.
    const missed = overdueBy(hub.nextWakeUp, useNow(30_000));

    return (
        <>
            <div
                ref={cardRef}
                className={
                    'bg-[#161c22] rounded-lg p-4 border ' +
                    // The card itself carries the alarm too, so the eye finds
                    // the right hub before it starts reading anything.
                    (missed ? 'border-[#d9534f]' : 'border-[#1e242a]')
                }
            >
                <div className="flex items-center justify-between gap-6 flex-wrap">
                    <div className="flex items-center gap-3 min-w-0">
                        {/* The hub's own icon is the way into everything about
                            it that is not a number to glance at — its 4G kit,
                            its branches, its update interval. Those are read
                            deliberately and one hub at a time, so they sit
                            behind this rather than on every row of the page. */}
                        <button
                            type="button"
                            onClick={() => setShowDetails(true)}
                            title="Hub settings — 4G, deployment branches, code updates"
                            className="h-9 w-9 rounded-md bg-[#1a2620] flex items-center justify-center flex-shrink-0 border border-transparent hover:border-[#74c69d] transition-colors"
                        >
                            <RouterIcon className="h-4 w-4 text-[#74c69d]" />
                        </button>
                        <div className="min-w-0">
                            <div className="text-[10px] uppercase tracking-wider text-[#6b7480]">
                                Hub · v{hub.version}
                            </div>
                            <div className="font-mono text-sm font-semibold text-white truncate">
                                {hub.macAddress}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-6 flex-shrink-0">
                        <HubMetric
                            label="Free mem"
                            value={
                                hub.availableMemoryGB != null
                                    ? `${hub.availableMemoryGB} GB`
                                    : null
                            }
                        />
                        <div className="flex flex-col items-end">
                            <div className="text-[10px] uppercase tracking-wider text-[#6b7480]">
                                Battery
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setShowHistory(true)}
                                title="View status history"
                                className="h-6 px-2 mt-0.5 text-sm tabular-nums bg-[#0f141a] border-[#1e242a] text-[#d8dde2] hover:bg-[#1a2126] hover:text-white"
                            >
                                {hub.batteryVoltage != null
                                    ? `${hub.batteryVoltage}V`
                                    : '—'}
                            </Button>
                        </div>
                        <HubMetric
                            label="Temp"
                            value={
                                hub.temperatureCelsius != null
                                    ? `${hub.temperatureCelsius}°C`
                                    : null
                            }
                        />
                        {/* Telemetry like the two above it — it moves with the
                            weather and the season — so it is read at a glance
                            rather than kept behind the settings dialog. */}
                        <HubMetric
                            label="Signal"
                            value={
                                hub.modemSignalQuality != null
                                    ? `${hub.modemSignalQuality}%`
                                    : null
                            }
                        />
                        <HubMetric
                            label="Last seen"
                            value={
                                hub.lastSeenAt
                                    ? utcStamp(hub.lastSeenAt, true)
                                    : null
                            }
                        />
                        <HubSchedule hub={hub} />
                        <NextWakeUp hub={hub} missed={missed} />
                        <Button
                            variant="ghost"
                            size="icon"
                            title="Clean up status history"
                            onClick={() => setShowCleanup(true)}
                            className="h-7 w-7 text-[#a8b0bb] hover:text-white hover:bg-[#1a2126]"
                        >
                            <EraserIcon className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            title="View pictures"
                            onClick={() => setShowImages(true)}
                            className="h-7 w-7 text-[#a8b0bb] hover:text-white hover:bg-[#1a2126]"
                        >
                            <CameraIcon className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            title="View logs"
                            onClick={() => setShowLogs(true)}
                            className="h-7 w-7 text-[#a8b0bb] hover:text-white hover:bg-[#1a2126]"
                        >
                            <BookOpenIcon className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            title="Incidents"
                            onClick={() => setShowIncidents(true)}
                            className="h-7 w-7 text-[#a8b0bb] hover:text-white hover:bg-[#1a2126]"
                        >
                            <SirenIcon className="h-3.5 w-3.5" />
                        </Button>
                    </div>
                </div>

                {(hub.slots.length > 0 || hub.note) && (
                    <div className="mt-4 pt-3 border-t border-[#1e242a]">
                        {/* The schedule the grid is counted against is up in
                            the header now, on the button that sets it; only a
                            reason there is no grid is left to say here. */}
                        {hub.note && (
                            <div className="flex justify-end mb-2">
                                <span className="text-[11px] text-[#6b7480]">
                                    {hub.note}
                                </span>
                            </div>
                        )}
                        {hub.slots.length > 0 && (
                            <DotStrip
                                sync={sync}
                                slots={hub.slots}
                                rows={STATUS_ROW}
                                // The card is wider than its samples by exactly
                                // this much; spend it on the gutter so the dots
                                // still line up with theirs.
                                indentPx={SAMPLE_INDENT_PX}
                            />
                        )}
                    </div>
                )}
            </div>

            <HubSettingsDialog
                hub={hub}
                open={showDetails}
                onClose={() => setShowDetails(false)}
            />

            <Dialog open={showHistory} onOpenChange={setShowHistory}>
                <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto bg-[#161c22] border-[#1e242a] text-[#d8dde2]">
                    <DialogHeader>
                        <DialogTitle className="text-white">
                            Hub status history{' '}
                            <span className="font-mono text-sm text-[#a8b0bb] ml-2">
                                {hub.macAddress}
                            </span>
                        </DialogTitle>
                    </DialogHeader>
                    {showHistory && (
                        <StatusHistoryTable macAddress={hub.macAddress} />
                    )}
                </DialogContent>
            </Dialog>

            <Dialog open={showCleanup} onOpenChange={setShowCleanup}>
                <DialogContent className="max-w-lg bg-[#161c22] border-[#1e242a] text-[#d8dde2]">
                    <DialogHeader>
                        <DialogTitle className="text-white">
                            Clean up status history{' '}
                            <span className="font-mono text-sm text-[#a8b0bb] ml-2">
                                {hub.macAddress}
                            </span>
                        </DialogTitle>
                    </DialogHeader>
                    {/* Remounts each time, so a previous run's result is gone. */}
                    {showCleanup && (
                        <HubStatusCleanup
                            macAddress={hub.macAddress}
                            onClose={() => setShowCleanup(false)}
                        />
                    )}
                </DialogContent>
            </Dialog>

            <HubPicturesDialog
                macAddress={hub.macAddress}
                open={showImages}
                onOpenChange={setShowImages}
            />

            <HubIncidentsDialog
                macAddress={hub.macAddress}
                open={showIncidents}
                onOpenChange={setShowIncidents}
            />

            <Dialog open={showLogs} onOpenChange={setShowLogs}>
                <DialogContent className="max-w-none w-[98vw] h-[95vh] flex flex-col p-4 bg-[#161c22] border-[#1e242a] text-[#d8dde2]">
                    <DialogHeader className="flex-shrink-0">
                        <DialogTitle className="text-white">
                            Hub logs{' '}
                            <span className="font-mono text-sm text-[#a8b0bb] ml-2">
                                {hub.macAddress}
                            </span>
                        </DialogTitle>
                    </DialogHeader>
                    {showLogs && <HubLogViewer macAddress={hub.macAddress} />}
                </DialogContent>
            </Dialog>
        </>
    );
}

/**
 * How far past its announced wake-up the hub is, or null while that wake-up is
 * still ahead (or within the grace period, or missing/unparseable).
 *
 * `nextWakeUp` comes off the hub's *latest* status, so it is always the last
 * plan we were told about: had the hub woken up since, it would have reported
 * and moved this forward. A stamp still sitting in the past therefore says the
 * hub did not come back — and, since nothing else will wake it, that it never
 * will. Both sides of the comparison are absolute instants, so the viewer's
 * zone plays no part in it.
 */
function overdueBy(nextWakeUp: string | null, nowMs: number): number | null {
    if (!nextWakeUp) return null;
    const wakeUpMs = Date.parse(nextWakeUp);
    if (Number.isNaN(wakeUpMs)) return null;
    const late = nowMs - wakeUpMs;
    return late > WAKE_UP_GRACE_MS ? late : null;
}

/**
 * The hub's own plan for coming back — and, once that plan has gone past with
 * no new status, the single most important thing on the page: this hub is not
 * waking up again by itself, and none of its dots matter until someone goes
 * out to it. So it stops being one metric among six and becomes an alarm.
 */
function NextWakeUp({
    hub,
    /** Milliseconds past the announced wake-up, or null if it is still ahead. */
    missed,
}: {
    hub: HubMonitoring;
    missed: number | null;
}) {
    const stamp = hub.nextWakeUp ? utcStamp(hub.nextWakeUp, true) : null;
    const offSchedule = hub.nextWakeUpOnSchedule === false;

    if (missed === null || !stamp) {
        return (
            <HubMetric
                label="Next wake-up"
                value={stamp}
                // The hub plans to come back when nothing is expected of it:
                // it will sleep through its slots.
                alert={offSchedule}
                title={
                    offSchedule
                        ? `Off schedule — ${hub.scheduleLabel ?? 'the acquisition schedule'} expects no wake-up then`
                        : undefined
                }
            />
        );
    }

    return (
        <div
            className="flex flex-col items-end"
            title={
                `The hub said it would wake up at ${stamp} and has reported ` +
                `nothing since — it is ${roughDuration(missed)} late. It is ` +
                `not coming back on its own.`
            }
        >
            <div className="text-[10px] uppercase tracking-wider font-semibold text-[#ff8a84]">
                Missed wake-up · {roughDuration(missed)} late
            </div>
            <div className="relative mt-0.5">
                {/* A halo that breathes, rather than the text: the stamp itself
                    has to stay perfectly readable while it draws the eye. */}
                <span
                    aria-hidden
                    className="absolute -inset-1 rounded bg-[#d9534f]/30 animate-pulse"
                />
                <div className="relative flex items-center gap-1.5 rounded bg-[#d9534f] px-2 py-0.5 text-sm font-bold tabular-nums text-white">
                    <TriangleAlertIcon className="h-3.5 w-3.5 flex-shrink-0" />
                    {stamp}
                </div>
            </div>
        </div>
    );
}

function HubMetric({
    label,
    value,
    alert,
    title,
}: {
    label: string;
    value: string | null;
    /** Show the value in red and bold: it needs looking at. */
    alert?: boolean;
    title?: string;
}) {
    return (
        <div className="flex flex-col items-end" title={title}>
            <div className="text-[10px] uppercase tracking-wider text-[#6b7480]">
                {label}
            </div>
            <div
                className={
                    alert
                        ? 'text-sm font-bold tabular-nums text-[#d9534f]'
                        : 'text-sm font-medium tabular-nums text-[#d8dde2]'
                }
            >
                {value ?? <span className="text-[#6b7480]">—</span>}
            </div>
        </div>
    );
}

function StatusHistoryTable({ macAddress }: { macAddress: string }) {
    const {
        data: statusHistory,
        isLoading,
        error,
    } = useHubStatusHistory(macAddress);

    if (isLoading)
        return <Skeleton className="w-full h-[200px] bg-[#1e242a]" />;
    if (error)
        return <div className="text-red-400 text-sm">{error.toString()}</div>;
    if (!statusHistory || statusHistory.length === 0)
        return (
            <div className="text-sm text-[#a8b0bb] py-6 text-center">
                No status history available
            </div>
        );

    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <thead className="bg-[#0f141a]">
                    <tr>
                        <TH>Recorded at (UTC)</TH>
                        <TH align="center">Battery (V)</TH>
                        <TH align="center">Temp (°C)</TH>
                        <TH align="center">Signal (%)</TH>
                    </tr>
                </thead>
                <tbody>
                    {statusHistory.map(
                        (entry: HubStatusHistoryModel, index: number) => (
                            <TR key={index}>
                                <TD className="text-xs">
                                    {utcStamp(entry.recordedAt, true)}
                                </TD>
                                <TD align="center" mono>
                                    {entry.batteryVoltage.toFixed(2)}V
                                </TD>
                                {/* Either can be missing: a hub on older
                                    firmware reports neither, and the status is
                                    still worth its row. */}
                                <TD align="center">
                                    {entry.temperatureCelsius != null
                                        ? `${entry.temperatureCelsius}°C`
                                        : '—'}
                                </TD>
                                <TD align="center" mono>
                                    {entry.modemSignalQuality != null
                                        ? `${entry.modemSignalQuality}%`
                                        : '—'}
                                </TD>
                            </TR>
                        ),
                    )}
                </tbody>
            </table>
        </div>
    );
}
